/**
 * Hook de gerenciamento de questionários.
 *
 * Responsabilidades:
 * - Orquestrar chamadas assíncronas (listar e excluir) via `useAsyncTask`.
 * - Manter estado de carregamento e erro para a UI.
 *
 * Observação:
 * - A UI/Dialogs (preview e confirmação de exclusão) ficam em `components/`.
 * - Este hook não decide a navegação nem manipula JSX.
 */

import { useCallback, useEffect, useMemo } from "react";

import { useAsyncTask } from "src-next/shared/utils/useAsyncTask";

import type { QuestionnairePayload } from "../../form-builder/types";
import { questionnaireManagementService } from "../services";

export interface UseQuestionnaireManagementParams {
  organizationId?: string;
}

export interface UseQuestionnaireManagementResult {
  questionnaires: QuestionnairePayload[];

  isLoading: boolean;
  errorMessage?: string;

  deleteQuestionnaire: (params: { questionnaireId: string }) => Promise<void>;
  isDeleting: boolean;
  deleteErrorMessage?: string;
}

function normalizeErrorMessage(error: unknown): string {
  if (typeof error === "string") return error;
  if (error instanceof Error) return error.message;
  return "Erro inesperado ao processar a requisição.";
}

export function useQuestionnaireManagement(
  params: UseQuestionnaireManagementParams = {},
): UseQuestionnaireManagementResult {
  const organizationId = params.organizationId?.trim() || "";

  // IMPORTANTE:
  // `useAsyncTask` memoriza `reset` baseado na referência de `initialData`.
  // Se passarmos `initialData: []` diretamente, esse array vira uma nova
  // referência a cada render, o que pode re-disparar o `useEffect` e gerar loop.
  const initialQuestionnairesData = useMemo<QuestionnairePayload[]>(
    () => [],
    [],
  );

  const loadQuestionnairesTask = useCallback(async () => {
    // Sem organização não existe escopo para listar questionários.
    // Retornamos lista vazia e evitamos chamadas com fallback "mágico".
    if (!organizationId) return [];

    const payloads =
      await questionnaireManagementService.listByOrganizationIdentifier(
        organizationId,
      );

    return payloads;
  }, [organizationId]);

  const {
    run: runList,
    loading: isLoading,
    data: questionnairesData,
    error: listError,
    reset: resetList,
  } = useAsyncTask<[], QuestionnairePayload[], string>({
    task: loadQuestionnairesTask,
    initialData: initialQuestionnairesData,
    mapError: normalizeErrorMessage,
  });

  useEffect(() => {
    // Recarrega quando organizationId muda.
    // Também evita chamada quando organizationId ainda não está disponível.
    if (!organizationId) return;

    runList();
    return () => {
      resetList();
    };
  }, [organizationId, runList, resetList]);

  const refresh = useCallback(async () => {
    await runList();
  }, [runList]);

  const {
    run: runDelete,
    loading: isDeleting,
    error: deleteError,
  } = useAsyncTask<[string], void, string>({
    task: async (questionnaireId) => {
      await questionnaireManagementService.removeById(questionnaireId);
    },
    mapError: normalizeErrorMessage,
  });

  const deleteQuestionnaire = useCallback(
    async ({ questionnaireId }: { questionnaireId: string }) => {
      await runDelete(questionnaireId);
      // Mantém consistência de UI após a deleção.
      await refresh();
    },
    [runDelete, refresh],
  );

  return {
    questionnaires: questionnairesData ?? [],
    isLoading,
    errorMessage: listError,
    deleteQuestionnaire,
    isDeleting,
    deleteErrorMessage: deleteError,
  };
}
